/**
 * Created by Ning Jiang on 2/7/2018.
 */

import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {BetLineEvent} from "@nolimitcity/slot-game/bin/core/betline/event/BetLineEvent";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {GameModeEvent} from "@nolimitcity/slot-game/bin/core/gamemode/event/GameModeEvent";
import {ReelEvent} from "@nolimitcity/slot-game/bin/core/reel/event/ReelEvent";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {GameSetting} from "@nolimitcity/slot-game/bin/core/setting/GameSetting";
import {SpinEvent} from "@nolimitcity/slot-game/bin/core/spin/event/SpinEvent";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {IWinRatioConfig} from "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio";
import {Power3, Timeline, TimelineLite, TweenLite} from "gsap";
import {CreepyCarnivalTwoGameConfig} from "../../CreepyCarnivalTwoGameConfig";
import {CreepyCarnivalTwoGameMode} from "../../gamemode/CreepyCarnivalTwoGameMode";
import {CCTwoStarSpinIntroEvents} from "../../starspin/controller/StarSpinIntroController";
import {CreepyCarnivalBulbsEvent} from "../../utils/CCTwoBulb";

export class CreepyCarnivalTwoBigWinView extends BaseView {

    private _layer:ScalableLayer;
    private _placeHolderText:PIXI.Text;
    private _isShowing:boolean = false;
    private _zoomCounter:number = 0;
    private _introFrame:PIXI.Sprite;
    private _bigWinText:PIXI.Sprite;
    private _introFrameContainer:PIXI.Container;
    private _bigWinTextContainer:PIXI.Container;
    private starAmount:number = 50;
    private cameraZ:number = 0;
    private fov:number = 20;
    private speed:number = 2.5;
    private stars:any = [];
    private starTexture:PIXI.Texture;
    private _screenWidth:number;
    private _screenHeight:number;

    constructor() {
        super();

        this._layer = StageManager.getLayer(CreepyCarnivalTwoGameConfig.instance.LAYERS.BIG_WIN.name);
        EventHandler.addEventListener(this, SpinEvent.IDLE, (event:GameEvent) => this.resetSymbolsToNormal());
        EventHandler.addEventListener(this, ReelEvent.ALL_REELS_SPIN_STOPPED, (event:GameEvent) => this.onReelsStopped());
    }

    public getBigWinLevelAnimation(winRatioConfig:IWinRatioConfig, level:number):Timeline {
        const timeline:TimelineLite = new TimelineLite();

        this.generateStarParticles();
        PIXI.Ticker.shared.autoStart = true; // have to auto-start it forcefully as it stopped working when updated the slot game to 0.36.1 version, weird behaviour :(

        PIXI.Ticker.shared.add(this.animateStar, this, PIXI.UPDATE_PRIORITY.HIGH);
        timeline.add([
            () => {
                this.displayBigWinBanner(winRatioConfig);
                this.updateBigWinLevelAnimation(winRatioConfig.text!);
            },
            //TweenLite.to(this._dimmer, 0.1, {alpha: 1}),
            TweenLite.to(this._placeHolderText.scale, winRatioConfig.duration, {x : 1})
        ]);

        // fading symbols only once.
        if(level === 0) {
            this.fadeSymbols();
        }

        return timeline;
    }

    public playEndingAnimation(endWinRatioConfig:IWinRatioConfig, mode:string, isAborted:boolean):void {
        const timeline:TimelineLite = new TimelineLite();
        let duration:number = 0.5, delay:number = 0.5;

        this.removeStarParticles();
        PIXI.Ticker.shared.remove(this.animateStar, this);

        if(isAborted) {
            if(this._introFrameContainer.scale.x > 1 && !this._isShowing) {
                this._introFrameContainer.scale.set(0, 1);
                this._bigWinTextContainer.scale.set(0, 0);
            }

            this._introFrameContainer.alpha = 1;
            this._bigWinTextContainer.alpha = 1;

            if(this._introFrameContainer.scale.x < 1) {
                this._introFrameContainer.scale.set(1, 1);
                this._bigWinTextContainer.scale.set(1, 1);
            }
        }

        if(GameSetting.isFastSpin) {
            timeline.add(() => EventHandler.dispatchEvent(new GameEvent(BetLineEvent.HIDE_ALL_BET_LINES)));
        }
        timeline.add([
            () => {
                if(!isAborted) {
                    this.updateBigWinLevelAnimation(endWinRatioConfig.text!);
                }
            },
            () => {
                if(mode === CreepyCarnivalTwoGameMode.STAR_SPIN) {
                    EventHandler.dispatchEvent(new GameEvent(GameModeEvent.CHANGE_MODE, {newMode : "NORMAL"}));
                    EventHandler.dispatchEvent(new GameEvent(CCTwoStarSpinIntroEvents.EXIT_FROM_STAR_SPIN));
                }
            },
            TweenLite.to(this._bigWinTextContainer, duration, {alpha : 0, delay : delay}),
            TweenLite.to(this._introFrameContainer, duration, {
                alpha : 0,
                delay : delay,
                onComplete : () => {
                    this._introFrameContainer.scale.set(0, 1);
                    this._bigWinTextContainer.scale.set(0, 0);
                    TweenLite.killTweensOf(this._bigWinTextContainer);
                    TweenLite.killTweensOf(this._introFrameContainer);
                }
            })
        ]);
        timeline.add(() => this._isShowing = false);

        timeline.play();
    }

    protected initAnimations():void {
        this._introFrame = new PIXI.Sprite(GameResources.getTextures("assets/bigwin_frame")[0]);
        this._introFrame.anchor.set(0.5);
        const introFrameContainer:PIXI.Container = new PIXI.Container();
        const bigwinFrameContainer:PIXI.Container = new PIXI.Container();
        this._bigWinText = new PIXI.Sprite(GameResources.getTextures("assets/bigwin")[0]);
        this._bigWinText.anchor.set(0.5);
        introFrameContainer.addChild(this._introFrame);
        bigwinFrameContainer.addChild(this._bigWinText);

        this._placeHolderText = new PIXI.Text("", {"fontSize" : 180, "fill" : "#ffffff"});
        this._placeHolderText.anchor.set(0.5, 0.5);
        this._placeHolderText.alpha = 0;
        this._layer.addChild(this._placeHolderText);
        this._introFrameContainer = introFrameContainer;
        this._bigWinTextContainer = bigwinFrameContainer;
        this._introFrameContainer.position.set(CreepyCarnivalTwoGameConfig.instance.GAME_WIDTH / 2, CreepyCarnivalTwoGameConfig.instance.GAME_HEIGHT * 0.4);
        this._introFrameContainer.scale.set(0, 1);
        this._bigWinTextContainer.position = this._introFrameContainer.position;
        this._introFrameContainer.alpha = 0;
        this._bigWinTextContainer.alpha = 0;
    }

    protected onResize(resizeData:IResizeData):void {
        this._screenWidth = Math.ceil(resizeData.width / this._layer.scale.x);
        this._screenHeight = Math.ceil(resizeData.height / this._layer.scale.y);
    }

    private displayBigWinBanner(winRatioConfig:IWinRatioConfig):void {
        if(this._introFrameContainer.scale.x > 1 && !this._isShowing) {
            this._introFrameContainer.scale.set(0, 1);
            this._bigWinTextContainer.scale.set(0, 0);
        }
        this._introFrameContainer.alpha = 1;
        this._bigWinTextContainer.alpha = 1;
        TweenLite.to(this._introFrameContainer.scale, 0.5, {x : 1});
        TweenLite.to(this._bigWinTextContainer.scale, winRatioConfig.duration, {x : 1, y : 1, ease : Power3.easeOut});
    }

    private fadeSymbols():void {
        for(let k:number = 0; k < CreepyCarnivalTwoGameConfig.instance.REELS_NUM; k++) {
            Reels.getSymbol(k, 0) && Reels.getSymbol(k, 0)!.changeState({
                state : GameConfig.instance.SYMBOL_STATES.noWin!,
                fadeInDuration : 1.5
            });
        }
    }

    private resetSymbolsToNormal():void {
        for(let k:number = 0; k < CreepyCarnivalTwoGameConfig.instance.REELS_NUM; k++) {
            Reels.getSymbol(k, 0) && Reels.getSymbol(k, 0)!.changeState({
                state : GameConfig.instance.SYMBOL_STATES.normal,
                fadeInDuration : 0
            });
        }
    }

    private generateStarParticles():void {
        if(this.stars.length < this.starAmount) {
            this.starTexture = GameResources.getTextures("assets/particle_star")[0];
            for(let i = 0; i < this.starAmount; i++) {
                let star = {
                    sprite : new PIXI.Sprite(this.starTexture),
                    z : 0,
                    x : 0,
                    y : 0
                };
                star.sprite.anchor.x = 0.5;
                star.sprite.anchor.y = 0.7;
                star.sprite.visible = false;
                this.randomizeStar(star, true);
                this._layer.addChild(star.sprite);
                this.stars.push(star);
            }
        }

        this._layer.addChild(this._introFrameContainer);
        this._layer.addChild(this._bigWinTextContainer);
    }

    private animateStar():void {
        this.cameraZ += 10 * (this.speed + 2);
        for(let i = 0; i < this.starAmount; i++) {
            let star = this.stars[i];
            star.sprite.visible = true;
            if(star.z < this.cameraZ)
                this.randomizeStar(star);

            //Map star 3d position to 2d with really simple projection
            let z = star.z - this.cameraZ;
            star.sprite.x = star.x * (this.fov / z) * this._screenWidth + CreepyCarnivalTwoGameConfig.instance.GAME_WIDTH / 2;
            star.sprite.y = star.y * (this.fov / z) * this._screenHeight + CreepyCarnivalTwoGameConfig.instance.GAME_HEIGHT / 2;

            //Calculate star rotation.
            let dxCenter = star.sprite.x - this._screenWidth / 2;
            let dyCenter = star.sprite.y - this._screenHeight / 2;
            star.sprite.rotation = Math.atan2(dyCenter, dxCenter) + Math.PI / 2;
        }
    }

    private removeStarParticles():void {
        this.stars.forEach(function(star:any) {
            star.sprite.destroy();
            star.sprite = null;
        }.bind(this));
        EventHandler.dispatchEvent(new GameEvent(CreepyCarnivalBulbsEvent.HIDE_YELLOW_BULBS_FOR_BIG_WIN));
        while(this.stars.length) {
            this.stars.pop();
        }
    }

    private randomizeStar(star:any, initial:boolean = false):void {
        star.z = initial ? Math.random() * 2000 : this.cameraZ + Math.random() * 1000 + 2000;

        //Calculate star positions with radial random coordinate so no star hits the camera.
        let deg = Math.random() * Math.PI * 2;
        let distance = Math.random() * 50 + 1;
        star.x = Math.cos(deg) * distance;
        star.y = Math.sin(deg) * distance;
    }

    private updateBigWinLevelAnimation(text:string):void {
        if(this._zoomCounter > 0) {
            TweenLite.to(this._introFrameContainer.scale, 0.2, {
                x : 1.2,
                y : 1.2,
                onComplete : () => {
                    TweenLite.to(this._introFrameContainer.scale, 0.1, {
                        x : 1.15,
                        y : 1.15,
                        onComplete : () => {
                            if(this._zoomCounter === 2) {
                                this._zoomCounter = 0;
                            }
                        }
                    })
                }
            });
            TweenLite.to(this._bigWinTextContainer.scale, 0.2, {
                x : 1.2,
                y : 1.2,
                onComplete : () => {
                    TweenLite.to(this._bigWinTextContainer.scale, 0.1, {
                        x : 1.15,
                        y : 1.15
                    })
                }
            });
        } else {
            this._zoomCounter++;
        }
        this._placeHolderText.text = text;
        this._isShowing = true;
    }

    private onReelsStopped():void {
        this.cameraZ = 0;
    }
}